import React, { useCallback, useState, useMemo } from 'react'
import { graphql } from 'react-relay'
import { object, string } from 'yup'

import { useNavigate, useLocation, Location } from 'react-router-dom'

import { YStack, XStack } from 'tamagui'
import { Form } from '@newComponents/Form/Form'

import { H2, Paragraph, Text } from '@newComponents/Text'
import { Link } from '@newComponents/Link'

import { useAuthContext } from '@data/useAuthContext'
import { useMutation } from '@data/useMutation'

import { EmojiIcon } from '@newComponents/EmojiIcon/EmojiIcon'

import { Spinner } from '@newComponents/Spinner'

import { useAuthRedirect } from '@data/useAuthRedirect'

import {
  SignInScreenEmailPasswordFormMutation,
  SignInScreenEmailPasswordFormMutation$data,
} from './__generated__/SignInScreenEmailPasswordFormMutation.graphql'

const VALIDATION_SCHEMA = object().shape({
  email: string().required('Required'),
  password: string().required('Required'),
})

export const SignInScreenEmailPasswordForm = ({
  initialEmail = '',
  setEmail,
}: {
  initialEmail?: string
  setEmail?: (email: string) => any
}) => {
  const location = useLocation()

  const authRedirectPromise = useAuthRedirect()

  const { setAuthTokens } = useAuthContext()
  const signInWithEmailPasswordMutation =
    useMutation<SignInScreenEmailPasswordFormMutation>(
      graphql`
        mutation SignInScreenEmailPasswordFormMutation(
          $input: SignInWithEmailPasswordMutationInput!
        ) {
          signInWithEmailPassword(input: $input) {
            __typename

            ... on SignInWithEmailPasswordMutationSuccess {
              accessToken
              refreshToken
              query {
                viewer {
                  id
                  ...UserPrimaryPhotoNew_user
                }
              }
            }
          }
        }
      `
    )

  const [formError, setFormError] =
    useState<SignInScreenEmailPasswordFormMutation$data | null>(null)

  const onEmailChange = useCallback(
    (value: string) => {
      if (setEmail) {
        setEmail(value)
      }
    },
    [setEmail]
  )

  const handleSubmit = useCallback(
    async (values: { email: string; password: string }) => {
      setFormError(null)

      const result = await signInWithEmailPasswordMutation({
        variables: {
          input: { email: values.email, password: values.password },
        },

        // Unfortunately Relay doesn't know that the `viewer` on this mutation is
        // the same as the `viewer` on the `Query` root. Therefore we tell Relay.
        updater(store) {
          const payload = store.getRootField('signInWithEmailPassword')

          if (
            payload &&
            payload.getValue('__typename') ===
              'SignInWithEmailPasswordMutationSuccess'
          ) {
            const query = payload.getLinkedRecord('query')

            if (query) {
              const root = store.getRoot()

              root.copyFieldsFrom(query)
            }
          }
        },
      })

      if (
        result.signInWithEmailPassword.__typename !==
        'SignInWithEmailPasswordMutationSuccess'
      ) {
        setFormError(result)
        return
      }

      await setAuthTokens(
        result.signInWithEmailPassword.accessToken,
        result.signInWithEmailPassword.refreshToken
      )

      await authRedirectPromise
    },
    [setFormError, setAuthTokens, authRedirectPromise]
  )

  return (
    <Form
      initialValues={{ email: initialEmail, password: '' }}
      onSubmit={handleSubmit}
      validationSchema={VALIDATION_SCHEMA}
    >
      <YStack gap='$6'>
        <FormError result={formError} location={location} />

        <Form.Field name='email' label='E-mail'>
          <Form.TextInput
            autoFocus
            placeholder='Enter your e-mail address'
            inputMode='email'
            submitBehaviour='submit-form'
            onChange={onEmailChange}
          />
        </Form.Field>

        <Form.Field name='password' label='Password'>
          <Form.TextInput submitBehaviour='submit-form' secureTextEntry />
        </Form.Field>

        <Form.SubmitButton>
          <Form.SubmitButton.Text paddingHorizontal='$2'>
            Sign in
          </Form.SubmitButton.Text>
        </Form.SubmitButton>
      </YStack>
    </Form>
  )
}

const FormError = ({
  result,
  location,
}: {
  result: SignInScreenEmailPasswordFormMutation$data | null
  location: Location
}) => {
  const routerState = useMemo(
    () => ({
      state: {
        backgroundLocation:
          (location.state?.backgroundLocation as Location) || undefined,

        authRedirectPath: location.state?.authRedirectPath as string,
      },
      replace: true,
    }),
    [location]
  )

  if (result === null) {
    return null
  }

  return (
    <XStack gap='$2' backgroundColor='$red1' borderRadius='$6' padding='$4'>
      <EmojiIcon size='$8' name='man-shrugging' />

      <YStack gap='$2' flex={1}>
        <Text size='$4'>
          We couldn't find an account with that email and password combination!
        </Text>

        <Link size='$4' href='/sign-in?method=email' options={routerState}>
          Did you want to try using your email instead?
        </Link>

        <Text size='$4'>
          Once you're in, head over to User Settings to reset your password.
        </Text>
      </YStack>
    </XStack>
  )
}
